import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { diffLines } from 'diff';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Tabs,
} from '../../components/ui';
import { PolicyMarkdown, processPolicyContent } from '../PolicyPage/policyMarkdown';
import { POLICY_KIND_TITLES } from '../../services/policyService/policyKinds';
import {
  ADMIN_POLICY_ACCEPTANCES,
  ADMIN_POLICY_ACCEPTANCES_CSV,
  ADMIN_POLICY_DOCUMENTS,
  ADMIN_POLICY_EVENTS,
  ADMIN_POLICY_VERSIONS,
  DISCARD_POLICY_DRAFT,
  PUBLISHER_ACKNOWLEDGMENT,
  SAVE_POLICY_DRAFT,
  PUBLISH_POLICY_VERSION,
} from '../../services/policyService/queries';
import { isAcknowledgmentRequired } from '../../services/policyService/checkoutPolicies';
import { graphqlErrorCode, graphqlErrorMessage } from '../../utils/listingHold';
import { PublishDialog } from './PublishDialog';
import { policyTimeline } from './policyAdmin';

const Editor = styled.textarea`
  ${tw`w-full border border-border-default rounded-md p-3 font-mono text-sm`}
  min-height: 280px;
`;

const DiffLine = styled.pre<{ $kind: 'add' | 'del' | 'same' }>`
  ${tw`text-xs whitespace-pre-wrap m-0 px-2`}
  background: ${({ $kind }) =>
    $kind === 'add' ? '#dcfce7' : $kind === 'del' ? '#fee2e2' : 'transparent'};
`;

type Doc = {
  kind: string;
  scope: string;
  title: string;
  current?: { id: string; contentMarkdown?: string | null; versionNumber?: number | null } | null;
  draft?: {
    id: string;
    contentMarkdown?: string | null;
    effectiveDate?: string | null;
    changeSummary?: string | null;
  } | null;
};

export default function AdminPoliciesPage() {
  const [tab, setTab] = useState<'edit' | 'history' | 'acceptances'>('edit');
  const [selectedKind, setSelectedKind] = useState<string | null>(null);
  const [draftText, setDraftText] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [ackToken, setAckToken] = useState(0);
  const [email, setEmail] = useState('');
  const [bookingId, setBookingId] = useState('');

  const documentsQuery = useQuery(ADMIN_POLICY_DOCUMENTS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const documents = (documentsQuery.data?.adminPolicyDocuments ?? []) as Doc[];
  const selected = documents.find(doc => doc.kind === selectedKind) ?? documents[0] ?? null;
  const kind = selected?.kind ?? null;
  const text =
    draftText ?? selected?.draft?.contentMarkdown ?? selected?.current?.contentMarkdown ?? '';

  const versionsQuery = useQuery(ADMIN_POLICY_VERSIONS, {
    variables: { kind, scope: selected?.scope },
    skip: !kind || tab !== 'history',
    fetchPolicy: 'network-only',
  });
  const eventsQuery = useQuery(ADMIN_POLICY_EVENTS, {
    variables: { kind, scope: selected?.scope, limit: 50 },
    skip: !kind || tab !== 'history',
    fetchPolicy: 'network-only',
  });
  const ackQuery = useQuery(PUBLISHER_ACKNOWLEDGMENT, {
    skip: !publishOpen,
    fetchPolicy: 'network-only',
  });
  const acceptanceFilter = {
    kind: kind || undefined,
    userEmail: email.trim() || undefined,
    bookingId: bookingId.trim() || undefined,
    limit: 50,
  };
  const acceptancesQuery = useQuery(ADMIN_POLICY_ACCEPTANCES, {
    variables: { filter: acceptanceFilter },
    skip: tab !== 'acceptances' || !kind,
    fetchPolicy: 'network-only',
  });

  const refetchDocs = () => documentsQuery.refetch();
  const [saveDraft, saveState] = useMutation(SAVE_POLICY_DRAFT);
  const [discardDraft] = useMutation(DISCARD_POLICY_DRAFT);
  const [publish, publishState] = useMutation(PUBLISH_POLICY_VERSION);
  const { refetch: fetchCsv } = useQuery(ADMIN_POLICY_ACCEPTANCES_CSV, {
    variables: { filter: acceptanceFilter },
    skip: true,
  });

  const live = selected?.current?.contentMarkdown ?? '';
  const diff = useMemo(() => diffLines(live, text), [live, text]);
  const timeline = policyTimeline({
    versions: versionsQuery.data?.adminPolicyVersions ?? [],
    events: eventsQuery.data?.adminPolicyEvents ?? [],
  });

  const onSave = async () => {
    if (!kind) return;
    try {
      await saveDraft({
        variables: {
          input: {
            kind,
            scope: selected?.scope,
            contentMarkdown: text,
            effectiveDate: selected?.draft?.effectiveDate || undefined,
            changeSummary: selected?.draft?.changeSummary || undefined,
          },
        },
      });
      toast.success('Draft saved');
      setDraftText(null);
      await refetchDocs();
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'The draft could not be saved'));
    }
  };

  const onPublish = async (input: {
    draftId: string;
    expectedCurrentVersionId: string | null;
    changeSummary: string;
    effectiveDate: string;
    materialChange: boolean;
    acknowledgmentVersionId: string;
    platform: 'web';
  }) => {
    try {
      await publish({ variables: { input } });
      toast.success('Published');
      setPublishOpen(false);
      setDraftText(null);
      await refetchDocs();
    } catch (error) {
      if (isAcknowledgmentRequired(error)) {
        setAckToken(token => token + 1);
        await ackQuery.refetch();
        toast.error('The acknowledgment changed. Read the current one and tick the box again.');
        return;
      }
      if (graphqlErrorCode(error) === 'POLICY_VERSION_STALE') {
        toast.error(
          graphqlErrorMessage(
            error,
            'This draft is behind the published version. Reload and try again.',
          ),
        );
        await refetchDocs();
        return;
      }
      toast.error(graphqlErrorMessage(error, 'This version could not be published'));
    }
  };

  const downloadCsv = async () => {
    try {
      const result = await fetchCsv({ filter: acceptanceFilter });
      const url = result.data?.adminPolicyAcceptancesCsv;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'The export could not be created'));
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Policies"
        description="Draft, compare and publish the documents this brand shows."
      />
      {documentsQuery.loading && !documents.length ? (
        <LoadingBlock label="Loading policies" />
      ) : null}
      {documentsQuery.error && !documents.length ? (
        <EmptyState
          title="Policies could not be loaded"
          description={documentsQuery.error.message}
        />
      ) : null}
      <Stack $gap={4}>
        <div tw="flex flex-wrap gap-2">
          {documents.map(doc => (
            <Button
              key={doc.kind}
              type="button"
              theme={doc.kind === selected?.kind ? 'filled' : 'outlined'}
              text={doc.title || POLICY_KIND_TITLES[doc.kind] || doc.kind}
              onClick={() => {
                setSelectedKind(doc.kind);
                setDraftText(null);
              }}
            />
          ))}
        </div>
        {selected && (
          <Card>
            <CardHeader
              title={selected.title || POLICY_KIND_TITLES[selected.kind]}
              description={
                selected.current?.versionNumber != null
                  ? `Live version ${selected.current.versionNumber}`
                  : 'No published version yet'
              }
            />
            <CardBody>
              <Tabs
                aria-label="Policy sections"
                items={[
                  { key: 'edit', label: 'Editor' },
                  { key: 'history', label: 'History' },
                  { key: 'acceptances', label: 'Acceptances' },
                ]}
                value={tab}
                onChange={setTab}
              />
              {tab === 'edit' && (
                <Stack $gap={4}>
                  <p tw="text-sm text-text-secondary" data-testid="policy-editor-count">
                    {text.length} characters · {text.split('\n').length} lines
                  </p>
                  <Editor
                    value={text}
                    onChange={event => setDraftText(event.target.value)}
                    data-testid="policy-editor"
                  />
                  <div tw="flex flex-wrap gap-2">
                    <Button
                      text="Save draft"
                      type="button"
                      onClick={onSave}
                      isLoading={saveState.loading}
                      dataTestId="policy-save-draft"
                    />
                    {selected.draft?.id && (
                      <Button
                        text="Discard draft"
                        theme="secondary"
                        type="button"
                        onClick={async () => {
                          await discardDraft({ variables: { draftId: selected.draft!.id } });
                          setDraftText(null);
                          await refetchDocs();
                        }}
                      />
                    )}
                    <Button
                      text="Publish"
                      type="button"
                      isDisabled={!selected.draft?.id}
                      onClick={() => setPublishOpen(true)}
                      dataTestId="policy-publish-open"
                    />
                  </div>
                  <div>
                    <p tw="text-sm font-medium mb-2">Preview</p>
                    <PolicyMarkdown content={processPolicyContent(text)} />
                  </div>
                  <div data-testid="policy-diff">
                    <p tw="text-sm font-medium mb-2">Changes from the live version</p>
                    {diff.map((part, index) => (
                      <DiffLine
                        key={index}
                        $kind={part.added ? 'add' : part.removed ? 'del' : 'same'}
                      >
                        {part.value}
                      </DiffLine>
                    ))}
                  </div>
                </Stack>
              )}
              {tab === 'history' && (
                <Stack $gap={4}>
                  {timeline.map(item => (
                    <p key={item.id} tw="text-sm">
                      <strong>{item.label}</strong>
                      {item.detail ? ` — ${item.detail}` : ''}
                    </p>
                  ))}
                  {!timeline.length && <p tw="text-sm text-text-secondary">No versions yet.</p>}
                </Stack>
              )}
              {tab === 'acceptances' && (
                <Stack $gap={4}>
                  <div tw="flex flex-wrap gap-2">
                    <input
                      placeholder="Email"
                      value={email}
                      onChange={event => setEmail(event.target.value)}
                      tw="border rounded-md px-2 py-1 text-sm"
                    />
                    <input
                      placeholder="Booking id"
                      value={bookingId}
                      onChange={event => setBookingId(event.target.value)}
                      tw="border rounded-md px-2 py-1 text-sm"
                    />
                    <Button
                      text="Download CSV"
                      type="button"
                      theme="secondary"
                      onClick={downloadCsv}
                    />
                  </div>
                  <Table>
                    <THead>
                      <TR>
                        <TH>When</TH>
                        <TH>Who</TH>
                        <TH>Context</TH>
                        <TH>Version</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {(acceptancesQuery.data?.adminPolicyAcceptances?.rows ?? []).map(
                        (row: any) => (
                          <TR key={row.id}>
                            <TD>{row.acceptedAt}</TD>
                            <TD>{row.userEmail}</TD>
                            <TD>{row.context}</TD>
                            <TD>{row.version?.versionNumber}</TD>
                          </TR>
                        ),
                      )}
                    </TBody>
                  </Table>
                </Stack>
              )}
            </CardBody>
          </Card>
        )}
      </Stack>
      <PublishDialog
        key={ackToken}
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        acknowledgment={ackQuery.data?.publisherAcknowledgment ?? null}
        draftId={selected?.draft?.id ?? null}
        expectedCurrentVersionId={selected?.current?.id ?? null}
        publishing={publishState.loading}
        onPublish={onPublish}
      />
    </PageContainer>
  );
}
